import React, { Component, ReactNode } from 'react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        this.props.onError?.(error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className='h-full bg-vscode-bg flex items-center justify-center'>
                    <div className='text-center max-w-md mx-auto p-6'>
                        <div className='text-vscode-error text-4xl mb-4'>⚠️</div>
                        <div className='text-vscode-text text-lg font-medium mb-2'>Error Loading Page</div>
                        <div className='text-vscode-text-muted text-sm mb-4'>
                            Something went wrong while loading this component. Please try refreshing the page.
                        </div>
                        {this.state.error && (
                            <details className='text-left'>
                                <summary className='text-vscode-text-muted text-xs cursor-pointer hover:text-vscode-text mb-2'>
                                    Technical Details
                                </summary>
                                <div className='text-vscode-text-muted text-xs font-mono bg-vscode-bg-secondary p-3 rounded border border-vscode-border overflow-auto'>
                                    {this.state.error.message}
                                </div>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
